/**
 * EIGENBEWEGUNG einer Ebene (Jans Wunsch 28.09.2026): eine Bewegung, die NACH
 * DER UHR laeuft statt nach dem Scrollen — z. B. ein Asset, das sich staendig
 * um sich selbst dreht, oder eines, das in Schleife „atmet" (Deckkraft auf und
 * ab). Sie legt sich OBENDRAUF: Rotation dreht um die EIGENE Achse der
 * Ebene (auch wenn das Scrollen sie gekippt hat), Deckkraft wird
 * multipliziert. Die Scroll-Keyframes bleiben unberuehrt.
 *
 * Stufe 1: Rotation und Deckkraft. Stufe 2: Position („Schweben", addiert)
 * und Skalierung („Pulsieren", multipliziert). Die Uhr startet, sobald die Ebene
 * sichtbar wird (Zeitfenster from..to erreicht), und beginnt beim Verlassen
 * neu. Wer am Geraet „Bewegung reduzieren" eingestellt hat, sieht sie nicht.
 */

/** Endlos · Einmal · Hin und zurueck */
export type EigenbewegungAblauf = "loop" | "once" | "pingpong";

export interface EigenbewegungRotation {
    active: boolean;
    /** Drehachse */
    axis: "x" | "y" | "z";
    /** Grad pro Durchgang (360 = einmal ganz herum, negativ = andersherum) */
    degrees: number;
    /** Dauer eines Durchgangs in ms */
    durationMs: number;
    mode: EigenbewegungAblauf;
    /** weich an- und auslaufen statt gleichmaessig */
    ease?: boolean;
}

export interface EigenbewegungDeckkraft {
    active: boolean;
    /** tiefster Wert 0..1 (1 = keine Aenderung) */
    min: number;
    durationMs: number;
    mode: EigenbewegungAblauf;
    ease?: boolean;
}

/** Stufe 2 (Jan 28.09.2026): „Schweben" — auf einer Achse hin und her. */
export interface EigenbewegungPosition {
    active: boolean;
    axis: "x" | "y" | "z";
    /** Strecke pro Durchgang in EDITOR-EINHEITEN (1 Einheit = 2048 px), negativ = andersherum */
    distance: number;
    durationMs: number;
    mode: EigenbewegungAblauf;
    ease?: boolean;
}

/** Stufe 2: „Pulsieren" — groesser und wieder kleiner. */
export interface EigenbewegungSkalierung {
    active: boolean;
    /** Faktor am Ende des Durchgangs (1,05 = 5 % groesser; < 1 = kleiner) */
    max: number;
    durationMs: number;
    mode: EigenbewegungAblauf;
    ease?: boolean;
}

export interface LayerEigenbewegung {
    rotation?: EigenbewegungRotation;
    opacity?: EigenbewegungDeckkraft;
    position?: EigenbewegungPosition;
    scale?: EigenbewegungSkalierung;
}

/** Vorlage „Schweben": 60 px hoch und zurueck in 2 × 2 s, weich. */
export const EIGENBEWEGUNG_SCHWEBEN: EigenbewegungPosition = {
    active: true,
    axis: "y",
    distance: 60 / 2048,
    durationMs: 2000,
    mode: "pingpong",
    ease: true,
};

/** Vorlage „Pulsieren": bis 105 % und zurueck in 2 × 1 s, weich. */
export const EIGENBEWEGUNG_PULSIEREN: EigenbewegungSkalierung = {
    active: true,
    max: 1.05,
    durationMs: 1000,
    mode: "pingpong",
    ease: true,
};

/** Vorlage „Drehen": einmal ganz herum in 6 s, endlos, gleichmaessig. */
export const EIGENBEWEGUNG_DREHEN: EigenbewegungRotation = {
    active: true,
    axis: "y",
    degrees: 360,
    durationMs: 6000,
    mode: "loop",
    ease: false,
};

/** Vorlage „Atmen": 100 → 40 % → 100 % in 2 × 1,5 s, weich. */
export const EIGENBEWEGUNG_ATMEN: EigenbewegungDeckkraft = {
    active: true,
    min: 0.4,
    durationMs: 1500,
    mode: "pingpong",
    ease: true,
};

/** Fortschritt 0..1 nach `vergangenMs` — je nach Ablauf und Kurve. */
export function eigenbewegungPhase(
    vergangenMs: number,
    durationMs: number,
    mode: EigenbewegungAblauf,
    ease = false,
): number {
    const d = durationMs > 0 ? durationMs : 1;
    const p = Math.max(0, vergangenMs) / d;
    let x: number;
    if (mode === "once") x = Math.min(p, 1);
    else if (mode === "pingpong") {
        const q = p % 2;
        x = q <= 1 ? q : 2 - q;
    } else x = p % 1;
    return ease ? 0.5 - 0.5 * Math.cos(Math.PI * x) : x;
}

const RAD = Math.PI / 180;

/**
 * Dreht um die EIGENE Achse der Ebene (Jans Fund 01.10.2026): die Scroll-
 * Winkel sind Euler-Winkel in der Reihenfolge XYZ (so setzen sie alle
 * Renderer), also haengen die drei Achsen aneinander. Schlicht addieren
 * stimmt deshalb nur, solange die Scroll-Drehung die gewaehlte Achse nicht
 * verkippt — bei einer per Scroll um Y ≈ 90° gedrehten Kugel fielen X und Z
 * zusammen („die Achsen reagieren nicht"). Sonst wird die Eigenbewegung als
 * eigene Drehung HINTER die Scroll-Drehung gesetzt und das Ergebnis wieder
 * in XYZ-Winkel zurueckgerechnet.
 */
function dreheUmEigeneAchse(grad: number[], achse: number, winkel: number): number[] {
    const [a = 0, b = 0, c = 0] = grad;
    // Z ist die innerste Achse, Y ohne Z-Drehung, X ohne Y/Z-Drehung:
    // dort IST Addieren die Drehung um die eigene Achse (und bleibt exakt)
    if (achse === 2 || (achse === 1 && c === 0) || (achse === 0 && b === 0 && c === 0)) {
        const rot = [...grad];
        rot[achse] = (rot[achse] ?? 0) + winkel;
        return rot;
    }
    // Matrix der Scroll-Drehung, three.js-Konvention fuer Euler 'XYZ'
    const ca = Math.cos(a * RAD), sa = Math.sin(a * RAD);
    const cb = Math.cos(b * RAD), sb = Math.sin(b * RAD);
    const cc = Math.cos(c * RAD), sc = Math.sin(c * RAD);
    const m = [
        [cb * cc, -cb * sc, sb],
        [ca * sc + sa * cc * sb, ca * cc - sa * sb * sc, -sa * cb],
        [sa * sc - ca * cc * sb, sa * cc + ca * sb * sc, ca * cb],
    ];
    const cw = Math.cos(winkel * RAD), sw = Math.sin(winkel * RAD);
    const e =
        achse === 0
            ? [[1, 0, 0], [0, cw, -sw], [0, sw, cw]]
            : [[cw, 0, sw], [0, 1, 0], [-sw, 0, cw]];
    const r = m.map((zeile) => [0, 1, 2].map((j) => zeile[0] * e[0][j] + zeile[1] * e[1][j] + zeile[2] * e[2][j]));
    // zurueck in Euler XYZ (wie THREE.Euler.setFromRotationMatrix)
    const y = Math.asin(Math.min(1, Math.max(-1, r[0][2])));
    let x: number, z: number;
    if (Math.abs(r[0][2]) < 0.9999999) {
        x = Math.atan2(-r[1][2], r[2][2]);
        z = Math.atan2(-r[0][1], r[0][0]);
    } else {
        x = Math.atan2(r[2][1], r[1][1]);
        z = 0;
    }
    return [x / RAD, y / RAD, z / RAD, ...grad.slice(3)];
}

/**
 * Wendet die Eigenbewegung auf die Scroll-Werte einer Ebene an. Rotation in
 * GRAD (wie layer3D sie liefert), Deckkraft 0..1. Gibt ein NEUES Objekt
 * zurueck, wenn sich etwas aendert.
 */
export function wendeEigenbewegungAn<
    T extends {
        rotation?: number[] | null;
        opacity?: number | null;
        position?: number[] | null;
        scale?: number[] | null;
    },
>(werte: T, eb: LayerEigenbewegung | null | undefined, vergangenMs: number): T {
    if (!eb) return werte;
    let aus = werte;
    const p = eb.position;
    if (p?.active && p.distance && Array.isArray(werte.position)) {
        const weg = p.distance * eigenbewegungPhase(vergangenMs, p.durationMs, p.mode, p.ease);
        const i = p.axis === "x" ? 0 : p.axis === "z" ? 2 : 1;
        const pos = [...werte.position];
        pos[i] = (pos[i] ?? 0) + weg;
        aus = { ...aus, position: pos };
    }
    const s = eb.scale;
    if (s?.active && s.max !== 1 && Array.isArray(werte.scale)) {
        const faktor = 1 + (s.max - 1) * eigenbewegungPhase(vergangenMs, s.durationMs, s.mode, s.ease);
        aus = { ...aus, scale: werte.scale.map((v) => v * faktor) };
    }
    const r = eb.rotation;
    if (r?.active && r.degrees && Array.isArray(werte.rotation)) {
        const winkel = r.degrees * eigenbewegungPhase(vergangenMs, r.durationMs, r.mode, r.ease);
        const i = r.axis === "x" ? 0 : r.axis === "z" ? 2 : 1;
        aus = { ...aus, rotation: dreheUmEigeneAchse(werte.rotation, i, winkel) };
    }
    const o = eb.opacity;
    if (o?.active && typeof werte.opacity === "number") {
        const min = Math.min(1, Math.max(0, o.min));
        const faktor = 1 - (1 - min) * eigenbewegungPhase(vergangenMs, o.durationMs, o.mode, o.ease);
        aus = { ...aus, opacity: Math.min(1, Math.max(0, werte.opacity * faktor)) };
    }
    return aus;
}
